import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { vendorService } from '../../services/vendorService';
import { formatCurrency } from '../../utils/formatters';
import { api } from '../../services/api';

const inputStyle: React.CSSProperties = {
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '9px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  display: 'block',
  marginBottom: 6,
};

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  whiteSpace: 'nowrap',
  textAlign: 'left',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  borderBottom: '1px solid rgba(241,245,249,1)',
  verticalAlign: 'middle',
};

const cardStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid rgba(92,78,51,0.15)',
  borderRadius: 6,
};

const primaryBtnStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d97706, #b45309)',
  color: '#1c2033',
  border: 'none',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

const ghostBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#6b6a5c',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

const statValueStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: 28,
  fontWeight: 300,
  background: 'linear-gradient(135deg, #d97706, #fbbf24)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  lineHeight: 1.2,
};

const filterSectionStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid rgba(92,78,51,0.15)',
  borderRadius: 6,
  padding: 20,
};

const errorStyle: React.CSSProperties = {
  background: 'rgba(201,90,90,0.08)',
  border: '1px solid rgba(201,90,90,0.2)',
  borderRadius: 4,
  padding: 16,
};

function getPaymentStatusStyle(status: string): React.CSSProperties {
  const base: React.CSSProperties = {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 3,
    fontFamily: "'Inter', sans-serif",
    fontSize: 9,
    fontWeight: 500,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
  };
  switch (status) {
    case 'PAID':
      return { ...base, background: 'rgba(90,201,140,0.12)', color: '#5ac98c', border: '1px solid rgba(90,201,140,0.25)' };
    case 'PENDING':
      return { ...base, background: 'rgba(92,78,51,0.22)', color: '#d97706', border: '1px solid rgba(92,78,51,0.35)' };
    case 'OVERDUE':
      return { ...base, background: 'rgba(201,90,90,0.12)', color: '#c95a5a', border: '1px solid rgba(201,90,90,0.25)' };
    default:
      return { ...base, background: 'rgba(122,106,85,0.15)', color: '#6b6a5c', border: '1px solid rgba(122,106,85,0.25)' };
  }
}

export const VendorReports: React.FC = () => {
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedBill, setSelectedBill] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: new Date().toISOString().split('T')[0]
  });
  const [activeReport, setActiveReport] = useState<'transactions' | 'performance' | 'bills'>('transactions');

  // Load vendors
  const { data: vendorsData, isLoading: vendorsLoading } = useQuery({
    queryKey: ['vendors'],
    queryFn: () => vendorService.getVendors({ limit: 1000 })
  });

  // Load vendor transaction report
  const { data: transactionReportData, isLoading: transactionReportLoading, error: transactionReportError } = useQuery({
    queryKey: ['vendorTransactionReport', selectedVendor, dateRange.startDate, dateRange.endDate],
    queryFn: async () => {
      const params = new URLSearchParams({
        ...(selectedVendor && { vendorId: selectedVendor }),
        ...(dateRange.startDate && { startDate: dateRange.startDate }),
        ...(dateRange.endDate && { endDate: dateRange.endDate })
      });

      const response = await api.get(`/reports/vendor-transactions?${params}`);
      return response.data.data;
    },
    enabled: true
  });

  const reports = [
    { id: 'transactions', label: 'Vendor Transactions' },
    { id: 'performance', label: 'Performance Analysis' },
    { id: 'bills', label: 'Bill History' }
  ];

  const handleExport = async (format: 'pdf' | 'excel') => {
    try {
      let endpoint = '';
      let filename = '';

      switch (activeReport) {
        case 'transactions':
          endpoint = '/reports/vendor-transactions';
          filename = `vendor-transactions.${format === 'excel' ? 'xlsx' : 'pdf'}`;
          break;
        case 'performance':
          endpoint = '/reports/vendor-performance';
          filename = `vendor-performance.${format === 'excel' ? 'xlsx' : 'pdf'}`;
          break;
        case 'bills':
          endpoint = '/reports/bill-history';
          filename = `bill-history.${format === 'excel' ? 'xlsx' : 'pdf'}`;
          break;
        default:
          endpoint = '/reports/vendor-transactions';
          filename = `vendor-transactions.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      }

      const params = new URLSearchParams({
        format,
        ...(selectedVendor && { vendorId: selectedVendor }),
        ...(dateRange.startDate && { startDate: dateRange.startDate }),
        ...(dateRange.endDate && { endDate: dateRange.endDate }),
        ...(selectedBill && activeReport === 'bills' && { billNumber: selectedBill })
      });

      const response = await api.get(`${endpoint}?${params}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      console.log(`Successfully exported vendor ${activeReport} as ${format}`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  const renderTransactionReport = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Filters */}
      <div style={filterSectionStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          <div>
            <label style={labelStyle}>Select Vendor</label>
            <select
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
              style={{ ...inputStyle, appearance: 'none' }}
              disabled={vendorsLoading}
            >
              <option value="">All Vendors</option>
              {vendorsData?.data?.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>From Date</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>To Date</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      {/* Summary heading */}
      <div>
        <h3 style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 20,
          fontWeight: 300,
          color: '#1c2033',
          margin: '0 0 16px 0',
        }}>
          Vendor Transaction Summary
        </h3>

        {transactionReportLoading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{ ...cardStyle, padding: 20, position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'rgba(92,78,51,0.25)' }} />
                <div style={{ height: 10, background: 'rgba(92,78,51,0.15)', borderRadius: 2, marginBottom: 10, width: '60%' }} />
                <div style={{ height: 28, background: 'rgba(239,232,213,0.6)', borderRadius: 2, width: '80%' }} />
              </div>
            ))}
          </div>
        ) : transactionReportError ? (
          <div style={errorStyle}>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#c95a5a', margin: 0 }}>
              Error loading transaction data
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {/* Total Bills */}
            <div style={{ ...cardStyle, padding: 20, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #c95a5a, rgba(201,90,90,0.3))' }} />
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 10px 0' }}>
                Total Bills
              </p>
              <p style={{ ...statValueStyle, fontSize: 32, margin: 0 }}>
                {transactionReportData?.summary?.totalBills || 0}
              </p>
            </div>

            {/* Total Amount */}
            <div style={{ ...cardStyle, padding: 20, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #d97706, #fbbf24)' }} />
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 10px 0' }}>
                Total Amount
              </p>
              <p style={{ ...statValueStyle, margin: 0 }}>
                {formatCurrency(transactionReportData?.summary?.totalAmount || 0)}
              </p>
            </div>

            {/* Payments Made */}
            <div style={{ ...cardStyle, padding: 20, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #5ac98c, rgba(90,201,140,0.3))' }} />
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 10px 0' }}>
                Payments Made
              </p>
              <p style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 28,
                fontWeight: 300,
                color: '#5ac98c',
                margin: 0,
                lineHeight: 1.2,
              }}>
                {formatCurrency(transactionReportData?.summary?.paymentsMade || 0)}
              </p>
            </div>

            {/* Outstanding */}
            <div style={{ ...cardStyle, padding: 20, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #c95a5a, rgba(201,90,90,0.3))' }} />
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 10px 0' }}>
                Outstanding
              </p>
              <p style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 28,
                fontWeight: 300,
                color: '#c95a5a',
                margin: 0,
                lineHeight: 1.2,
              }}>
                {formatCurrency(transactionReportData?.summary?.outstanding || 0)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Transactions table */}
      <div style={cardStyle}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
          <h3 style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 20,
            fontWeight: 300,
            color: '#1c2033',
            margin: 0,
          }}>
            Recent Vendor Transactions
          </h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(248,250,252,1)' }}>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Vendor</th>
                <th style={thStyle}>Bill Number</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Payment Status</th>
                <th style={thStyle}>Days Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {transactionReportLoading ? (
                [1, 2, 3, 4, 5].map(i => (
                  <tr key={i}>
                    {[1, 2, 3, 4, 5, 6].map(j => (
                      <td key={j} style={tdStyle}>
                        <div style={{ height: 12, background: 'rgba(239,232,213,0.6)', borderRadius: 2, width: j === 4 ? '70%' : '85%' }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : transactionReportData?.transactions && transactionReportData.transactions.length > 0 ? (
                transactionReportData.transactions.map((transaction: any, index: number) => (
                  <tr
                    key={transaction.id || index}
                    style={{ transition: 'background 0.15s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(248,250,252,1)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={tdStyle}>{new Date(transaction.date).toLocaleDateString()}</td>
                    <td style={tdStyle}>{transaction.vendor}</td>
                    <td style={{ ...tdStyle, color: '#d97706', fontWeight: 500 }}>{transaction.billNumber}</td>
                    <td style={{ ...tdStyle, fontFamily: "'Inter', sans-serif", fontSize: 15, color: '#d97706', fontWeight: 400 }}>
                      {formatCurrency(transaction.amount)}
                    </td>
                    <td style={tdStyle}>
                      <span style={getPaymentStatusStyle(transaction.paymentStatus)}>
                        {transaction.paymentStatus}
                      </span>
                    </td>
                    <td style={tdStyle}>{transaction.daysOutstanding} days</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} style={{ ...tdStyle, textAlign: 'center', padding: '32px 16px', color: '#6b6a5c' }}>
                    {selectedVendor
                      ? `No transactions found for the selected vendor${dateRange.startDate ? ' in the specified date range' : ''}`
                      : `No transactions found${dateRange.startDate ? ' in the specified date range' : ''}`
                    }
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderPerformanceReport = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Performance KPIs */}
      <div>
        <h3 style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 20,
          fontWeight: 300,
          color: '#1c2033',
          margin: '0 0 16px 0',
        }}>
          Vendor Performance Metrics
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {/* Avg Payment Time */}
          <div style={{ ...cardStyle, padding: 20, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #5a8cc9, rgba(90,140,201,0.3))' }} />
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 10px 0' }}>
              Average Payment Time
            </p>
            <p style={{ ...statValueStyle, margin: 0 }}>18.5 days</p>
          </div>

          {/* On-Time Rate */}
          <div style={{ ...cardStyle, padding: 20, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #5ac98c, rgba(90,201,140,0.3))' }} />
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 10px 0' }}>
              On-Time Payment Rate
            </p>
            <p style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 28,
              fontWeight: 300,
              color: '#5ac98c',
              margin: 0,
              lineHeight: 1.2,
            }}>87%</p>
          </div>

          {/* Total Vendors */}
          <div style={{ ...cardStyle, padding: 20, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #d97706, #fbbf24)' }} />
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 10px 0' }}>
              Total Vendors
            </p>
            <p style={{ ...statValueStyle, fontSize: 32, margin: 0 }}>45</p>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Top vendors by volume */}
        <div style={cardStyle}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
            <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 300, color: '#1c2033', margin: 0 }}>
              Top 5 Vendors by Volume
            </h3>
          </div>
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { name: 'ABC Suppliers Ltd', amount: 25670.50, percentage: 28.7 },
              { name: 'XYZ Materials Co', amount: 18450.25, percentage: 20.6 },
              { name: 'Tech Solutions Inc', amount: 15230.75, percentage: 17.0 },
              { name: 'Office Supplies Pro', amount: 12890.40, percentage: 14.4 },
              { name: 'Equipment Rental LLC', amount: 8950.80, percentage: 10.0 }
            ].map((vendor, index) => (
              <div key={index} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 12px',
                background: 'rgba(248,250,252,1)',
                borderRadius: 4,
                border: '1px solid rgba(239,232,213,0.6)',
              }}>
                <div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#5b5a52', margin: '0 0 3px 0', fontWeight: 500 }}>
                    {vendor.name}
                  </p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', margin: 0 }}>
                    {vendor.percentage}% of total spend
                  </p>
                </div>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: '#d97706', fontWeight: 400 }}>
                  {formatCurrency(vendor.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Payment terms analysis */}
        <div style={cardStyle}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
            <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 300, color: '#1c2033', margin: 0 }}>
              Payment Terms Analysis
            </h3>
          </div>
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { terms: 'Net 30', count: 25, amount: 45670.50 },
              { terms: 'Net 15', count: 12, amount: 23450.25 },
              { terms: 'Net 60', count: 6, amount: 15230.75 },
              { terms: 'Due on Receipt', count: 2, amount: 5099.25 }
            ].map((term, index) => (
              <div key={index} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 12px',
                background: 'rgba(248,250,252,1)',
                borderRadius: 4,
                border: '1px solid rgba(239,232,213,0.6)',
              }}>
                <div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#5b5a52', margin: '0 0 3px 0', fontWeight: 500 }}>
                    {term.terms}
                  </p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', margin: 0 }}>
                    {term.count} vendors
                  </p>
                </div>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: '#5ac98c', fontWeight: 400 }}>
                  {formatCurrency(term.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderBillReport = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Search filter */}
      <div style={filterSectionStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'flex-end' }}>
          <div>
            <label style={labelStyle}>Bill Number</label>
            <input
              type="text"
              value={selectedBill}
              onChange={(e) => setSelectedBill(e.target.value)}
              placeholder="Enter bill number (e.g., BILL-2024-001)"
              style={inputStyle}
            />
          </div>
          <button style={primaryBtnStyle}>
            Search Bill
          </button>
        </div>
      </div>

      {/* Bill lifecycle */}
      <div>
        <h3 style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 20,
          fontWeight: 300,
          color: '#1c2033',
          margin: '0 0 16px 0',
        }}>
          Bill Payment History
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Bill Received */}
          <div style={{ ...cardStyle, padding: 16, borderLeft: '3px solid #d97706' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.15em', color: '#1c2033', margin: '0 0 6px 0', textTransform: 'uppercase' }}>
                  Bill Received
                </h4>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: '0 0 3px 0' }}>
                  February 1, 2024 at 3:20 PM
                </p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: 0 }}>
                  Vendor: ABC Suppliers Ltd
                </p>
              </div>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#d97706' }}>
                {formatCurrency(3450.75)}
              </span>
            </div>
          </div>

          {/* Bill Approved */}
          <div style={{ ...cardStyle, padding: 16, borderLeft: '3px solid #fbbf24' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.15em', color: '#1c2033', margin: '0 0 6px 0', textTransform: 'uppercase' }}>
                  Bill Approved
                </h4>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: '0 0 3px 0' }}>
                  February 3, 2024 at 11:30 AM
                </p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: 0 }}>
                  Approved by: Manager
                </p>
              </div>
              <span style={{
                display: 'inline-block',
                padding: '3px 10px',
                borderRadius: 3,
                fontFamily: "'Inter', sans-serif",
                fontSize: 9,
                fontWeight: 500,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                background: 'rgba(92,78,51,0.22)',
                color: '#d97706',
                border: '1px solid rgba(92,78,51,0.35)',
              }}>
                Approved
              </span>
            </div>
          </div>

          {/* Payment Scheduled */}
          <div style={{ ...cardStyle, padding: 16, borderLeft: '3px solid #5a8cc9' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.15em', color: '#1c2033', margin: '0 0 6px 0', textTransform: 'uppercase' }}>
                  Payment Scheduled
                </h4>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: '0 0 3px 0' }}>
                  February 28, 2024 (Due Date)
                </p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: 0 }}>
                  Payment Method: Bank Transfer
                </p>
              </div>
              <span style={{
                display: 'inline-block',
                padding: '3px 10px',
                borderRadius: 3,
                fontFamily: "'Inter', sans-serif",
                fontSize: 9,
                fontWeight: 500,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                background: 'rgba(90,140,201,0.12)',
                color: '#5a8cc9',
                border: '1px solid rgba(90,140,201,0.25)',
              }}>
                Scheduled
              </span>
            </div>
          </div>

          {/* Payment Completed */}
          <div style={{ ...cardStyle, padding: 16, borderLeft: '3px solid #5ac98c' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.15em', color: '#1c2033', margin: '0 0 6px 0', textTransform: 'uppercase' }}>
                  Payment Completed
                </h4>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: '0 0 3px 0' }}>
                  February 26, 2024 at 2:15 PM
                </p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: 0 }}>
                  Reference: TXN-240226-001
                </p>
              </div>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#5ac98c' }}>
                {formatCurrency(3450.75)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeReport) {
      case 'transactions':
        return renderTransactionReport();
      case 'performance':
        return renderPerformanceReport();
      case 'bills':
        return renderBillReport();
      default:
        return renderTransactionReport();
    }
  };

  return (
    <div style={{ padding: 24, background: '#f4efe2', minHeight: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 28,
              fontWeight: 300,
              color: '#1c2033',
              margin: 0,
              letterSpacing: '0.05em',
            }}>
              Vendor Reports
            </h2>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.3em', textTransform: 'uppercase', margin: '4px 0 0 0' }}>
              Payables Intelligence
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => handleExport('excel')}
              style={ghostBtnStyle}
            >
              Export Excel
            </button>
            <button
              onClick={() => handleExport('pdf')}
              style={primaryBtnStyle}
            >
              Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div style={{ borderBottom: '1px solid rgba(92,78,51,0.15)', marginBottom: 24, display: 'flex', gap: 4 }}>
        {reports.map((report) => {
          const isActive = activeReport === report.id;
          return (
            <button
              key={report.id}
              onClick={() => setActiveReport(report.id as any)}
              style={{
                padding: '10px 20px',
                fontFamily: "'Inter', sans-serif",
                fontSize: 10,
                fontWeight: 500,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                border: 'none',
                borderBottom: isActive ? '2px solid #d97706' : '2px solid transparent',
                background: isActive ? 'rgba(239,232,213,0.6)' : 'transparent',
                color: isActive ? '#d97706' : '#8a8778',
                transition: 'all 0.15s',
                marginBottom: -1,
                whiteSpace: 'nowrap',
              }}
            >
              {report.label}
            </button>
          );
        })}
      </div>

      {/* Report content */}
      {renderContent()}
    </div>
  );
};
